/*
  Frequently-bought-together cards: swap "Add to basket" for a quantity stepper
  once the item is in the basket, the way Amazon's side-by-side carousels do
  ("1 in basket" with a bin on the left, "- 2 in basket +" from two upwards).

  Clicking "Add to basket" flips the card to "1 in basket" immediately - no
  loading spinner, exactly like Amazon's search-result cards - and the
  /cart/add.js call happens in the background (the click is intercepted before
  the theme's <product-form> submit handler, which would otherwise block the
  flip on a full round trip behind its spinner). The basket popup still gets
  the same section-rendered update product-form.js would have given it.

  Clicking +/- likewise renders the new number immediately and only talks to
  the server after a short pause, coalescing a burst of clicks into one
  request for the final quantity. Plus/minus/bin go through /cart/change.js.
*/
(function () {
  var SECTION = '.tm_product_acces-grid';
  var CART_JSON = '/cart.js';
  var CART_CHANGE = '/cart/change.js';
  var DEBOUNCE_MS = 300;

  function cardsWithStepper() {
    return Array.prototype.slice.call(document.querySelectorAll(SECTION + ' .new_product_card_buttons'));
  }

  function partsOf(block) {
    return {
      stepper: block.querySelector('.fbt-basket-stepper'),
      buyWrap: block.querySelector('.buybuttonsnew'),
      submit: block.querySelector('.product-form__submit'),
      addSpinner: block.querySelector('.loading-overlay__spinner'),
    };
  }

  function getCart() {
    return fetch(CART_JSON, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(function (r) {
      return r.json();
    });
  }

  // The header bubble is refreshed the same way the Buy Again grid does it, so
  // the count stays right without re-rendering the whole cart section.
  function refreshCartCount(cart) {
    document.querySelectorAll('.cart-count-bubble, .cart-count').forEach(function (el) {
      var span = el.querySelector('span');
      if (span) span.innerText = cart.item_count;
      else el.innerText = cart.item_count;
      if (cart.item_count > 0) el.classList.remove('hidden');
    });
  }

  function cartDrawer() {
    var el = document.querySelector('cart-drawer');
    return el && typeof el.getSectionsToRender === 'function' && typeof el.renderContents === 'function'
      ? el
      : null;
  }

  // The basket popup is server-rendered Liquid ("Qty: {{ item.quantity }}",
  // the line price, the total), so changing the cart over AJAX leaves all of
  // it showing whatever the last render produced. product-form.js solves this
  // on "Add to basket" by asking /cart/add.js for the drawer's sections and
  // handing them to cart-drawer's renderContents(); a +/- has to do the same
  // or the popup keeps reporting the pre-click quantity.
  // Each id is sent once - Shopify caps a section-rendering request at five
  // sections and re-renders a duplicated id once per mention (the same trap
  // that made cart.js's quantity changes take seconds before it deduped).
  function drawerSectionIds(drawer) {
    var ids = [];
    drawer.getSectionsToRender().forEach(function (section) {
      if (ids.indexOf(section.id) === -1) ids.push(section.id);
    });
    return ids;
  }

  // suppressMobilePopup: client asked that deleting a line from a card shows
  // no popup. The existing open() stub below only covers desktop - on mobile
  // cart-drawer.js's renderContents doesn't call open() at all, it calls
  // amazonMobileCartPopup.show() instead, so that path needs stubbing too or
  // the "1 added to basket" card appears on a delete.
  function renderCartUI(cart, suppressMobilePopup) {
    var drawer = cartDrawer();
    if (!drawer || !cart.sections) {
      refreshCartCount(cart);
      return;
    }
    // renderContents also opens the drawer, which is what "Add to basket"
    // wants but a +/- on a card does not - the popup should stay exactly as
    // open or as closed as the shopper left it. Stub open() for this call and
    // restore it from a timeout queued after renderContents' own (same task
    // queue, so ours runs second).
    var originalOpen = drawer.open;
    drawer.open = function () {};

    var popup = window.amazonMobileCartPopup;
    var originalShow = popup && popup.show;
    if (suppressMobilePopup && originalShow) {
      popup.show = function () {};
    }

    try {
      drawer.renderContents(cart);
    } finally {
      setTimeout(function () {
        drawer.open = originalOpen;
        if (suppressMobilePopup && originalShow) {
          popup.show = originalShow;
        }
      }, 0);
    }
  }

  function lineFor(cart, variantId) {
    var id = String(variantId);
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === id) {
        return { item: cart.items[i], line: i + 1 };
      }
    }
    return null;
  }

  // Line numbers are 1-indexed positions in cart.items, which shift whenever
  // the cart changes anywhere (this card, another card, the basket popup).
  // Looking that up with a fresh /cart.js fetch on every click was the other
  // reason a click felt a beat slower than Amazon's; caching the last-known
  // line per variant turns a click into one request instead of two. A stale
  // cache entry is self-correcting - see sendChange() below.
  var lineCache = {};

  function cacheLines(cart) {
    lineCache = {};
    cart.items.forEach(function (item, i) {
      lineCache[String(item.variant_id)] = i + 1;
    });
  }

  function render(block, quantity) {
    var p = partsOf(block);
    if (!p.stepper) return;
    if (quantity > 0) {
      // Only touch the text when it actually changes - every write here is a
      // mutation the grid observer sees, and a same-value rewrite would just
      // schedule another pointless cart read.
      var qtyEl = p.stepper.querySelector('.fbt-stepper__qty');
      if (qtyEl.textContent !== String(quantity)) qtyEl.textContent = quantity;
      // at one, the left control removes the line entirely (Amazon shows a bin)
      p.stepper.classList.toggle('is-single', quantity === 1);
      p.stepper.classList.remove('hidden');
      if (p.buyWrap) p.buyWrap.classList.add('hidden');
    } else {
      p.stepper.classList.add('hidden');
      if (p.buyWrap) p.buyWrap.classList.remove('hidden');
    }
  }

  // A change that is still in flight owns the displayed number. Without this,
  // the drawer/tab-strip observers below could re-run syncAll from a cart read
  // that started before /cart/change.js had committed, and its (older) result
  // could land after the click's own render() - leaving the stepper one step
  // behind the cart.
  var pending = 0;

  function syncAll() {
    if (pending > 0) return Promise.resolve();
    var blocks = cardsWithStepper();
    if (!blocks.length) return Promise.resolve();
    return getCart()
      .then(function (cart) {
        cacheLines(cart);
        blocks.forEach(function (block) {
          var stepper = block.querySelector('.fbt-basket-stepper');
          if (!stepper) return;
          // A card with a click queued (debounce window) or a change in
          // flight owns its own number. The global `pending` guard doesn't
          // cover the debounce window - the grid observer fires 250ms after
          // the optimistic render, the flush only at 300ms, so this sync
          // used to read the not-yet-changed cart and flip the number BACK
          // for a beat before the real update landed (the visible blink).
          var state = stepState.get(stepper);
          if (state && (state.inFlight || state.target !== null)) return;
          var found = lineFor(cart, stepper.getAttribute('data-variant-id'));
          render(block, found ? found.item.quantity : 0);
        });
        refreshCartCount(cart);
      })
      .catch(function () {
        /* leave the card on its current state if the cart can't be read */
      });
  }

  // /cart/change.js against a stale cached line either no-ops or edits the
  // wrong line, and the response tells us which happened: if the item isn't
  // sitting at the quantity we asked for afterwards, the cache was wrong, so
  // look the line up for real and retry once. On a correct cache hit (the
  // common case) that second round trip never happens.
  function sendChange(variantId, quantity) {
    var drawer = cartDrawer();
    var extra = {};
    if (drawer) {
      extra.sections = drawerSectionIds(drawer);
      extra.sections_url = window.location.pathname;
    }

    function changeAtLine(line) {
      return fetch(CART_CHANGE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(Object.assign({ line: line }, extra, { quantity: quantity })),
      }).then(function (r) {
        return r.json();
      });
    }

    var cachedLine = lineCache[String(variantId)];
    var attempt = cachedLine
      ? changeAtLine(cachedLine)
      : getCart().then(function (cart) {
          var found = lineFor(cart, variantId);
          return found ? changeAtLine(found.line) : cart;
        });

    return attempt.then(function (cart) {
      if (!cart || !cart.items) return cart;
      var found = lineFor(cart, variantId);
      var achieved = found ? found.item.quantity : 0;
      if (achieved === quantity) return cart;
      return getCart().then(function (fresh) {
        var freshFound = lineFor(fresh, variantId);
        return freshFound ? changeAtLine(freshFound.line) : fresh;
      });
    });
  }

  var stepState = new WeakMap();

  function queueChange(stepper, variantId, block, quantity) {
    render(block, quantity);

    var state = stepState.get(stepper) || { target: null, inFlight: false, timer: null };
    state.target = quantity;
    stepState.set(stepper, state);

    clearTimeout(state.timer);
    state.timer = setTimeout(function () {
      flush(stepper, variantId, block);
    }, DEBOUNCE_MS);
  }

  function flush(stepper, variantId, block) {
    var state = stepState.get(stepper);
    // Already sending a request for this stepper - it will pick up whatever
    // state.target ends up being once it settles, below.
    if (!state || state.inFlight || state.target === null) return;

    // No spinner, no dimming - Amazon shows nothing while its stepper syncs.
    // The number already moved on the click; the request is invisible, and
    // clicks during it just move the target for the next flush.
    var quantity = state.target;
    state.inFlight = true;
    pending++;

    sendChange(variantId, quantity)
      .then(function (cart) {
        if (cart && cart.items !== undefined) {
          cacheLines(cart);
          // Only settle the displayed number from this response if no newer
          // click arrived while it was in flight. When one did, this response
          // is already stale - rendering it walked the count backwards for a
          // beat (click + at 3 -> shows 4, request for 3 lands -> flips back
          // to 3, re-flush pushes it to 4 again) on every fast click burst.
          // The finally below re-flushes to the newer target, and that
          // response does the render.
          if (state.target === quantity) {
            var found = lineFor(cart, variantId);
            render(block, found ? found.item.quantity : 0);
          }
          // quantity 0 is the bin - the line is being removed, so no "added to
          // basket" popup on mobile (client's requirement).
          renderCartUI(cart, quantity === 0);
        } else {
          return syncAll();
        }
      })
      .catch(function () {
        return syncAll();
      })
      .finally(function () {
        pending--;
        state.inFlight = false;
        if (state.target !== quantity) {
          // A newer click arrived mid-request - send its target right away.
          flush(stepper, variantId, block);
        } else {
          state.target = null;
        }
      });
  }

  // The optimistic add. The card flips to "1 in basket" on the click itself;
  // /cart/add.js runs behind it. Marked in-flight on the same per-stepper
  // state the +/- queue uses, so a + clicked while the add is still travelling
  // just raises the target and flush() sends it once the add has settled (by
  // then the line cache knows the new line, too).
  function addInBackground(stepper, block) {
    var variantId = stepper.getAttribute('data-variant-id');
    var state = stepState.get(stepper) || { target: null, inFlight: false, timer: null };
    stepState.set(stepper, state);
    state.inFlight = true;
    pending++;

    var drawer = cartDrawer();
    var body = { items: [{ id: Number(variantId), quantity: 1 }] };
    if (drawer) {
      body.sections = drawerSectionIds(drawer);
      body.sections_url = window.location.pathname;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (resp) {
        // /cart/add.js reports failures (sold out, limits) as {status, description}
        if (resp && resp.status) return syncAll();
        if (drawer && resp && resp.sections) {
          if (window.innerWidth <= 767) {
            // Mobile unchanged: renderContents hands off to the Amazon-style
            // popup rather than the drawer (cart-drawer.js only calls open()
            // above 767), and the drawer itself is closed behind it.
            drawer.renderContents(resp);
            if (typeof drawer.close === 'function') drawer.close();
          } else {
            // Client: on desktop an FBT add must NOT pop the basket open -
            // cart-drawer's open() puts .body_layer on <body>, so the whole
            // page greys out with nothing on screen explaining why. The
            // contents still have to be re-rendered (the popup is
            // server-rendered Liquid, so skipping it leaves the pre-add
            // quantities showing), which is exactly what renderCartUI does
            // for +/- already: render with open() stubbed out.
            renderCartUI(resp);
          }
        }
        return getCart().then(function (cart) {
          cacheLines(cart);
          // A + clicked mid-add owns the number now; don't fight it.
          if (state.target === null) {
            var found = lineFor(cart, variantId);
            render(block, found ? found.item.quantity : 0);
          }
          refreshCartCount(cart);
        });
      })
      .catch(function () {
        return syncAll();
      })
      .finally(function () {
        pending--;
        state.inFlight = false;
        if (state.target !== null) flush(stepper, variantId, block);
      });
  }

  // Capture phase, so this wins over product-form.js's submit flow entirely -
  // its spinner never shows and nothing blocks the flip.
  document.addEventListener(
    'click',
    function (e) {
      var addBtn = e.target.closest(SECTION + ' .product-form__submit');
      if (!addBtn) return;
      var block = addBtn.closest('.new_product_card_buttons');
      var stepper = block && block.querySelector('.fbt-basket-stepper');
      if (!stepper) return;
      e.preventDefault();
      e.stopPropagation();
      render(block, 1);
      addInBackground(stepper, block);
    },
    true
  );

  document.addEventListener('click', function (e) {
    var up = e.target.closest(SECTION + ' .fbt-stepper__up');
    var down = e.target.closest(SECTION + ' .fbt-stepper__down');
    if (!up && !down) return;
    e.preventDefault();
    var stepper = (up || down).closest('.fbt-basket-stepper');
    if (!stepper) return;
    var block = stepper.closest('.new_product_card_buttons');
    var variantId = stepper.getAttribute('data-variant-id');
    var current = parseInt(stepper.querySelector('.fbt-stepper__qty').textContent, 10) || 0;
    var next = up ? current + 1 : current - 1;
    if (next < 0) return;
    queueChange(stepper, variantId, block, next);
  });

  // product-form.js hides the add spinner again once its fetch settles, whether
  // it succeeded or failed - that transition is the cue to re-read the cart.
  function watchAddButtons() {
    cardsWithStepper().forEach(function (block) {
      var p = partsOf(block);
      if (!p.addSpinner || p.addSpinner.dataset.fbtWatched) return;
      p.addSpinner.dataset.fbtWatched = '1';
      new MutationObserver(function () {
        if (p.addSpinner.classList.contains('hidden')) {
          setTimeout(syncAll, 60);
        }
      }).observe(p.addSpinner, { attributes: true, attributeFilter: ['class'] });
    });
  }

  // The basket popup can change the same lines these cards track - its bin
  // button removes an item outright - and it re-renders itself when it does.
  // Without this the card would still be offering "2 in basket" for something
  // the shopper had just emptied out of the basket. syncAll() is a no-op while
  // one of our own changes is still in flight, so this only ever reacts to
  // edits made somewhere else.
  function watchCartDrawer() {
    var contents = document.getElementById('CartDrawer');
    if (!contents || contents.dataset.fbtWatched) return;
    contents.dataset.fbtWatched = '1';
    var timer = null;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(syncAll, 250);
    }).observe(contents, { childList: true, subtree: true });
  }

  function init() {
    if (!document.querySelector(SECTION)) return;
    watchAddButtons();
    watchCartDrawer();
    syncAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The tab strip swaps which products are in the DOM, so pick up new cards.
  // Debounced because render() edits this subtree itself - without it every
  // label update would immediately queue another cart read.
  //
  // data-variant-id is watched too: fbt-variant-sync.js rewrites it when the
  // main product's colour changes, so the card points at the matching colour
  // rather than whatever Liquid picked. The quantity shown is per-variant, so
  // without re-syncing here a card switched from Grey to White would keep
  // showing the Grey line's "N in basket". attributeFilter keeps this to that
  // one attribute - render() toggles classes in this same subtree, and
  // watching attributes broadly would make every label update re-trigger the
  // cart read this debounce exists to avoid.
  var root = document.querySelector(SECTION);
  if (root) {
    var reSyncTimer = null;
    new MutationObserver(function () {
      clearTimeout(reSyncTimer);
      reSyncTimer = setTimeout(function () {
        watchAddButtons();
        syncAll();
      }, 250);
    }).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-variant-id'],
    });
  }
})();
